import { useCallback } from "react";
import { type TabData } from "@utils/dashboard-storage";
import { TabsState } from "./reducer";
import { useTabsState } from "./use-tabs-state";
import { DraggedItem, DropPointer } from "./types";
import { buildNavItems, isSameTabOrder, moveTabToGroup, reorderNavItem } from "./group-utils";

/**
 * Two-level drag and drop over the nav item list (ungrouped tabs + group
 * blocks). Dragging a group moves the whole block; dragging a tab moves just
 * that tab, which also covers "reorder within a group" since moveTabToGroup
 * degrades to a plain reorder when the target group equals the source group.
 */
export const useTabGroupDnD = (
  state: TabsState,
  actions: ReturnType<typeof useTabsState>["actions"],
  onStructureChange: (nextTabs: TabData[], previousTabs: TabData[]) => void,
) => {
  const { localTabs, dragged } = state;

  const handleDragStart = useCallback(
    (e: React.DragEvent, item: DraggedItem) => {
      actions.dragStart(item);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", "");
    },
    [actions],
  );

  const handleDragEnd = useCallback(() => {
    actions.dragEnd();
  }, [actions]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDragEnter = useCallback(
    (e: React.DragEvent, target: DropPointer) => {
      e.preventDefault();
      actions.dragOver(target);
    },
    [actions],
  );

  // Deliberately a no-op — see the note on dragOverTarget in use-tabs-state.ts.
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const target = state.dragOver;
      if (!dragged || !target) return;

      const before = localTabs;
      let next: TabData[];

      if (dragged.type === "group") {
        const fromNavIndex = buildNavItems(before).findIndex(
          (item) => item.kind === "group" && item.groupName === dragged.groupName,
        );
        if (fromNavIndex === -1) return;
        next = reorderNavItem(before, fromNavIndex, target.navIndex);
      } else {
        next = moveTabToGroup(before, dragged.tabId, target.groupName, target.tabId);
      }

      actions.dragEnd();

      // Dropping back where it already was — nothing to save.
      if (isSameTabOrder(next, before)) return;

      actions.setLocalTabs(next);
      onStructureChange(next, before);
    },
    [dragged, state.dragOver, localTabs, actions, onStructureChange],
  );

  return {
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
  } as const;
};
